import {
  OHNE_TAETIGKEIT, OPT_BERUEHRUNGSPUNKTE, OPT_BERUFSSTATUS, OPT_FAMILIENSTAND, OPT_WICHTIG, OPT_ZIELE,
} from "../state/schema";
import { card, chipGroup, h, jaNein, note, selectInput, textareaInput, textInput } from "../components/ui";
import { vBerufFamilie, vStammdaten, vZiele } from "../logic/validation";
import { Step } from "./types";

/* ---------- 1 · Willkommen (Hero) ---------- */
export const stepWelcome: Step = {
  id: "welcome",
  section: null,
  chrome: false,
  title: "Deine Mehrwertanalyse",
  build(ctx) {
    const fact = (icon: string, title: string, text: string) =>
      h("div", { class: "fact" },
        h("div", { class: "fact__icon", text: icon }),
        h("div", {}, h("div", { class: "fact__title", text: title }), h("div", { class: "fact__text", text: text })));

    return h(
      "div",
      { class: "hero" },
      h("img", { class: "hero__logo", src: "/assets/logo-lockup.png", alt: "Blacksmith Finance" }),
      h("h1", { class: "hero__title", text: "Deine Mehrwertanalyse" }),
      h("p", {
        class: "hero__lead",
        text:
          "Mit dieser Mehrwertanalyse erfassen wir deine aktuelle finanzielle Situation, deine Absicherung, deine Ziele und deine bestehenden Verträge. So können wir deine nächste Beratung gezielt und effizient vorbereiten.",
      }),
      h(
        "div",
        { class: "hero__facts" },
        fact("⏱️", "Etwa 10–15 Minuten", "In ruhigen Schritten – du kannst jederzeit zurückgehen."),
        fact("📄", "Unterlagen bereithalten", "Aktuelle Gehaltsabrechnung, Versicherungsscheine sowie Strom- oder Gasabrechnung."),
        fact("💾", "Automatisch zwischengespeichert", "Deine Angaben bleiben erhalten, solange diese Seite geöffnet ist."),
        fact("⬆️", "Dokumente später hochladen", "Erforderliche Dokumente lädst du im Verlauf bequem hoch."),
      ),
      h("button", { class: "btn btn--primary hero__cta", type: "button", text: "Mehrwertanalyse starten", on: { click: () => ctx.goNext() } }),
      h("p", { class: "field__optional", style: { marginTop: "14px", textAlign: "center" }, text: "Deine Daten werden vertraulich behandelt und nur zur Vorbereitung deiner Beratung verwendet." })
    );
  },
};

/* ---------- 2 · Ziele & Erwartungen ---------- */
export const stepZiele: Step = {
  id: "ziele",
  section: "fundament",
  eyebrow: "Fundament",
  title: "Unsere Werte – deine Fahrtrichtung",
  subtitle: "Erzähl uns kurz, worum es dir geht. Mehrfachauswahl möglich.",
  leaveMessage: "Deine Richtung ist klar.",
  validate: vZiele,
  build(ctx) {
    const s = ctx.store;
    return h(
      "div",
      {},
      card(
        h("div", { class: "card__title", text: "Welche Berührungspunkte hattest du bisher?" }),
        h("p", { class: "card__hint", text: "Mit Finanzen, Versicherungen oder Förderungen. Optional." }),
        chipGroup(s, "ziele.beruehrungspunkte", OPT_BERUEHRUNGSPUNKTE)
      ),
      card(
        h("div", { class: "card__title", html: "Was ist dir bei einer Beratung besonders wichtig? <span style='color:var(--neg)'>*</span>" }),
        h("p", { class: "card__hint", text: "Mindestens eine Auswahl." }),
        chipGroup(s, "ziele.wichtig", OPT_WICHTIG)
      ),
      card(
        h("div", { class: "card__title", html: "Was möchtest du mit deiner Finanzplanung erreichen? <span style='color:var(--neg)'>*</span>" }),
        h("p", { class: "card__hint", text: "Mindestens eine Auswahl." }),
        chipGroup(s, "ziele.ziele", OPT_ZIELE),
        h("div", { style: { marginTop: "16px" } },
          textareaInput(s, "ziele.zieleFreitext", {
            label: "Möchtest du uns noch etwas zu deinen Zielen, Wünschen oder Träumen mitteilen?",
            optional: true,
            optionalText: "Optional – nur ausfüllen, wenn du möchtest.",
            placeholder: "Deine Gedanken …",
            rows: 3,
          }))
      )
    );
  },
};

/* ---------- 3 · Persönliche Angaben ---------- */
export const stepPersoenlich: Step = {
  id: "persoenlich",
  section: "fundament",
  eyebrow: "Fundament",
  title: "Persönliche Angaben",
  subtitle: "Damit wir wissen, mit wem wir es zu tun haben.",
  validate: vStammdaten,
  build(ctx) {
    const s = ctx.store;
    return card(
      h("div", { class: "grid-2" },
        textInput(s, "stammdaten.vorname", { label: "Vorname", required: true }),
        textInput(s, "stammdaten.nachname", { label: "Nachname", required: true })),
      h("div", { class: "grid-2" },
        textInput(s, "stammdaten.geburtsdatum", { label: "Geburtsdatum", required: true, type: "date" }),
        textInput(s, "stammdaten.geburtsort", { label: "Geburtsort", required: true })),
      textInput(s, "stammdaten.staatsangehoerigkeit", { label: "Staatsangehörigkeit", required: true }),
      h("div", { class: "grid-2" },
        textInput(s, "stammdaten.strasse", { label: "Straße", required: true }),
        textInput(s, "stammdaten.hausnummer", { label: "Hausnummer", required: true })),
      h("div", { class: "grid-2" },
        textInput(s, "stammdaten.plz", { label: "Postleitzahl", required: true, inputmode: "numeric" }),
        textInput(s, "stammdaten.ort", { label: "Ort", required: true })),
      h("div", { class: "grid-2" },
        textInput(s, "stammdaten.email", { label: "E-Mail-Adresse", required: true, type: "email", inputmode: "email" }),
        textInput(s, "stammdaten.mobil", { label: "Mobilnummer", required: true, type: "tel", inputmode: "tel" })),
      selectInput(s, "stammdaten.familienstand", OPT_FAMILIENSTAND, { label: "Familienstand", required: true })
    );
  },
};

/* ---------- 4 · Beruf & Familie ---------- */
export const stepBerufFamilie: Step = {
  id: "beruf-familie",
  section: "fundament",
  eyebrow: "Fundament",
  title: "Beruf & Familie",
  subtitle: "Ein paar Angaben zu deinem Umfeld.",
  leaveMessage: "Dein Fundament steht.",
  validate: vBerufFamilie,
  build(ctx) {
    const s = ctx.store;
    const kinderDetail = h("div");
    const renderKinder = () => {
      kinderDetail.innerHTML = "";
      if (s.get("stammdaten.hatKinder") === "ja") {
        kinderDetail.appendChild(
          h("div", { class: "grid-2", style: { marginTop: "12px" } },
            textInput(s, "stammdaten.anzahlKinder", { label: "Anzahl der Kinder", required: true, type: "number", inputmode: "numeric" }),
            textInput(s, "stammdaten.alterKinder", { label: "Alter der Kinder", required: true, placeholder: "z. B. 4 und 7" }))
        );
      }
    };
    renderKinder();

    // Die Berufsbezeichnung wird nur verlangt, wenn überhaupt eine Tätigkeit ausgeübt wird.
    const berufDetail = h("div");
    const renderBeruf = () => {
      berufDetail.innerHTML = "";
      if (s.get("stammdaten.berufsstatus") === OHNE_TAETIGKEIT) {
        // Sonst bliebe eine vorher eingetippte Tätigkeit im Datensatz stehen.
        if (s.get("stammdaten.berufsbezeichnung")) s.set("stammdaten.berufsbezeichnung", "");
      } else {
        berufDetail.appendChild(
          textInput(s, "stammdaten.berufsbezeichnung", {
            label: "Berufsbezeichnung / aktuelle Tätigkeit",
            required: true,
            placeholder: "z. B. Industriemechanikerin",
            hint: "Bitte so genau wie möglich – die genaue Tätigkeit entscheidet über deine Absicherung.",
          })
        );
      }
    };
    renderBeruf();

    return h(
      "div",
      {},
      card(
        h("div", { class: "card__title", html: "Hast du Kinder? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "stammdaten.hatKinder", renderKinder),
        kinderDetail
      ),
      card(
        h("div", { class: "card__title", html: "Berufliche Situation <span style='color:var(--neg)'>*</span>" }),
        h("p", { class: "card__hint", text: "Diese Angaben brauchen wir, um deinen Einkommensverlauf und deinen Absicherungsbedarf richtig einzuordnen." }),
        selectInput(s, "stammdaten.berufsstatus", OPT_BERUFSSTATUS, { label: "Aktueller Status", required: true, onInput: renderBeruf }),
        berufDetail,
        textInput(s, "stammdaten.abschluss", { label: "Schul- oder Studienabschluss", required: true, placeholder: "z. B. Abitur, Ausbildung, Bachelor" })
      ),
      note("info-dark", "Deine berufliche Situation bestimmt maßgeblich, welche Absicherung zu dir passt – deshalb fragen wir hier genauer nach.")
    );
  },
};
